import { createContext, useContext } from "react";
import {
  ReadContractorCard,
  UpdateContractorCard,
} from "@/types/contractorCard";
import { UseMutationResult, UseQueryResult } from "@tanstack/react-query";
import { ApiError } from "@/services/api";
import { PreviewState } from "@/components/file-preview/hooks/useFilePreview";

export type ContractorCardHandlerKey = "update";

export type ContractorCardLoadingReason =
  | "fetching_contractor_card"
  | "updating_contractor_card"
  | "unknown";

export interface ContractorCardLoadingState {
  isLoading: boolean;
  reason: ContractorCardLoadingReason;
}

export interface MutationsObject {
  updateContractorCardMutation: UseMutationResult<
    ReadContractorCard,
    ApiError,
    UpdateContractorCard,
    unknown
  >;
}

export interface QueriesObject {
  contractorCardQuery: UseQueryResult<ReadContractorCard, Error>;
}

export interface ContractorCardContextType {
  query: UseQueryResult<ReadContractorCard, Error>;
  data?: ReadContractorCard;
  errorState: { error: Error | null; isQuery: boolean };
  loadingState: ContractorCardLoadingState;

  handleUpdate: (updateData: UpdateContractorCard) => void;
  handleFileUpload: (content: string, filename: string) => Promise<void>;
  handleView: () => void;
  clearError: () => void;
  refreshContractorCard: () => void;
  previewDialog: PreviewState | null;
  handleClosePreview: () => void;
}

const ContractorCardContext = createContext<ContractorCardContextType>(
  {} as ContractorCardContextType,
);

export const useContractorCardContext = () => {
  const context = useContext(ContractorCardContext);
  if (!context) {
    throw new Error(
      "useContractorCardContext must be used within ContractorCardProvider",
    );
  }
  return context;
};

export { ContractorCardContext };
